import type { BriefDto } from '@/types/dto';
import { renderBriefMarkdown } from '@/lib/brief-renderer';
import { SourceList } from './SourceList';

const themeOrder = [
  'region',
  'price',
  'driver',
  'investment',
  'customer',
  'competitor',
  'regulation',
  'technology',
];

const themeLabel: Record<string, string> = {
  region: 'Region',
  price: 'Price',
  driver: 'Drivers',
  investment: 'Investment',
  customer: 'Customers',
  competitor: 'Competitors',
  regulation: 'Regulation',
  technology: 'Technology',
};

type Props = {
  brief: BriefDto;
  heading?: string;
};

export function BriefView({ brief, heading }: Props) {
  const { overall, overallCited, themes, citationMap, totalItems } = brief;

  const sourcesFor = (ids: string[]) => {
    const seen = new Set<string>();
    const out = [];
    for (const id of ids) {
      if (seen.has(id)) continue;
      seen.add(id);
      const c = citationMap[id];
      if (c) out.push(c);
    }
    return out;
  };

  return (
    <>
      <section className="editorial-card" aria-labelledby="brief-overall-title">
        <span className="eyebrow">Executive Summary</span>
        <h2
          id="brief-overall-title"
          className="font-(family-name:--font-heading) text-2xl md:text-3xl font-bold mt-2 mb-3"
        >
          {heading ?? 'The story across all eight themes'}
        </h2>
        <p className="text-muted text-xs tracking-widest uppercase mb-5 font-(family-name:--font-ui)">
          {brief.briefDate}
          {totalItems > 0 && (
            <>
              {' '}·{' '}
              <span className="text-text tabular-nums font-semibold">{totalItems}</span>{' '}
              items scored
            </>
          )}
        </p>
        <BriefProse html={renderBriefMarkdown(overall ?? '', citationMap)} withDropCap />
        <SourceList label="All sources" sources={sourcesFor(overallCited)} />
      </section>

      {themeOrder.map((key) => {
        const t = themes[key];
        if (!t || !t.summary.trim()) return null;
        return (
          <section key={key} className="editorial-card" aria-labelledby={`theme-${key}`}>
            <span className="eyebrow">{themeLabel[key] ?? key}</span>
            <h2
              id={`theme-${key}`}
              className="font-(family-name:--font-heading) text-2xl font-bold mt-2 mb-1 flex items-baseline gap-2 flex-wrap"
            >
              <span>{themeLabel[key] ?? key}</span>
              <span className="text-muted text-base font-normal tabular-nums">· {t.count} items</span>
            </h2>
            <BriefProse html={renderBriefMarkdown(t.summary, citationMap)} />
            <SourceList label="Sources" sources={sourcesFor(t.citedIds)} />
          </section>
        );
      })}
    </>
  );
}

function BriefProse({ html, withDropCap }: { html: string; withDropCap?: boolean }) {
  return (
    <div
      className={`brief-prose font-(family-name:--font-body) text-[1rem] md:text-[1.05rem] leading-[1.7] ${
        withDropCap ? 'brief-drop' : ''
      }`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
